import caretLeft from '@phosphor-icons/core/assets/bold/caret-left-bold.svg';
import caretRight from '@phosphor-icons/core/assets/bold/caret-right-bold.svg';

/*
 * Tab Scroll
 *
 * Tab strips scroll sideways when their tabs outgrow the container (pure CSS, in
 * legacy-ui/toggle and enhanced-ui/toggle). This module only adds what CSS can't do
 * on its own:
 *
 *   - two arrow buttons, appended to the strip's wrapper, that page the strip
 *   - mt-hasScrollPrev / mt-hasScrollNext on the wrapper while there is more to see
 *     in that direction. The stylesheets use them to show the arrows and edge fades,
 *     so at either end its arrow disappears and the first tab lines up with the
 *     content below
 *   - keeping the selected (and keyboard-focused) tab in view
 *
 * It binds itself to every matching strip in the document, including ones added
 * later (AJAX partials, panes, modals, Vue apps, bocaTabs addTab), so no per-page
 * script is needed. Two kinds of strip are recognised:
 *
 *   .mt-toggle              the standard tab set. Its .mt-toggle-content scrolls and
 *                           every child is a tab. Vertical (_vert) and box (_box)
 *                           tab sets don't scroll and are skipped.
 *
 *   .mt-tabscroll           opt-in for any other tab strip:
 *     .mt-tabscroll-content   the element that scrolls
 *     .mt-tabscroll-item      the tabs, when they aren't its direct children
 *
 * A tab counts as selected when it has mt-isActive, aria-current (other than
 * "false") or aria-selected="true".
 *
 * Buttons are appended after the scroller, never before it: bocaTabs treats the
 * first child of .mt-toggle as the tab list.
 */

const ACTIVE_CLASS = 'mt-isActive';
const PREV_CLASS = 'mt-hasScrollPrev';
const NEXT_CLASS = 'mt-hasScrollNext';
const BOUND_ATTR = 'data-tab-scroll';

const STRIPS = [
  {
    selector: '.mt-toggle',
    skip: root => root.classList.contains('mt-toggle_vert') || root.classList.contains('mt-toggle_box'),
    content: root => Array.from(root.children).find(child => child.classList.contains('mt-toggle-content')),
    items: content => Array.from(content.children),
  },
  {
    selector: '.mt-tabscroll',
    skip: () => false,
    content: root => root.querySelector('.mt-tabscroll-content'),
    items: content => {
      const tagged = content.querySelectorAll('.mt-tabscroll-item');
      return Array.from(tagged.length > 0 ? tagged : content.children);
    },
  },
];

const STRIP_SELECTOR = STRIPS.map(strip => strip.selector).join(', ');

const BUTTONS = {
  prev: { icon: caretLeft, label: 'Scroll tabs left' },
  next: { icon: caretRight, label: 'Scroll tabs right' },
};

// Sub-pixel tolerance: zoomed pages report fractional scroll positions.
const EDGE_TOLERANCE = 2;

const isActive = element =>
  element.classList.contains(ACTIVE_CLASS)
  || (element.hasAttribute('aria-current') && element.getAttribute('aria-current') !== 'false')
  || element.getAttribute('aria-selected') === 'true';

// Whether a mutation's old attribute value marked the element as selected.
function wasActive(mutation) {
  const old = mutation.oldValue;
  switch (mutation.attributeName) {
    case 'class':
      return (old || '').split(/\s+/).includes(ACTIVE_CLASS);
    case 'aria-current':
      return old !== null && old !== 'false';
    case 'aria-selected':
      return old === 'true';
    default:
      return false;
  }
}

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function createButton(direction) {
  const { icon, label } = BUTTONS[direction];
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `mt-toggle-scrollBtn mt-toggle-scrollBtn_${direction}`;
  button.setAttribute('aria-label', label);
  button.innerHTML = icon;

  const svg = button.firstElementChild;
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  return button;
}

// The arrows are styled by modern.css / alis-modern.css. A page that loads this
// module without the tab styles in scope (a popup, a print view) would show bare
// native buttons, so check that the styles apply before adding anything.
function stylesApply(root) {
  const probe = createButton('next');
  probe.style.visibility = 'hidden';
  root.appendChild(probe);
  const applies = window.getComputedStyle(probe).position === 'absolute';
  probe.remove();
  return applies;
}

export class TabScroll {
  constructor(root, content, strip) {
    this.toggle = root;
    this.content = content;
    this.strip = strip;
    this.frame = null;
    this.hasRevealedInitialTab = false;

    this.prev = createButton('prev');
    this.next = createButton('next');
    root.append(this.prev, this.next);

    this.prev.addEventListener('click', () => this.page(-1));
    this.next.addEventListener('click', () => this.page(1));
    content.addEventListener('scroll', () => this.schedule(), { passive: true });

    // A tab reached with the keyboard may sit under an arrow; bring it out.
    content.addEventListener('focusin', event => {
      const item = this.items().find(candidate => candidate.contains(event.target));
      if (item) {
        this.reveal(item, false);
      }
    });

    // Tabs added, removed or renamed (bocaTabs, Vue, Knockout) change the scroll
    // width without resizing anything, and a newly selected tab may be out of view.
    this.mutationObserver = new MutationObserver(mutations => this.onMutations(mutations));
    this.mutationObserver.observe(content, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'aria-current', 'aria-selected'],
      attributeOldValue: true,
    });

    TabScroll.resizeObserver?.observe(root);
    TabScroll.resizeObserver?.observe(content);

    root.setAttribute(BOUND_ATTR, 'true');

    // Tabs that were selected before this ran (server-rendered).
    const active = this.items().find(isActive);
    if (active) {
      this.reveal(active, false);
      this.hasRevealedInitialTab = true;
    }

    this.update();
  }

  onMutations(mutations) {
    const items = this.items();
    const activated = mutations
      .filter(mutation => mutation.type === 'attributes')
      .filter(mutation => items.includes(mutation.target))
      .filter(mutation => isActive(mutation.target) && !wasActive(mutation))
      .map(mutation => mutation.target)
      .pop();

    this.schedule();

    if (activated) {
      this.reveal(activated, this.hasRevealedInitialTab);
      this.hasRevealedInitialTab = true;
    }
  }

  items() {
    return this.strip.items(this.content);
  }

  schedule() {
    if (this.frame !== null) {
      return;
    }
    this.frame = window.requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    const { content, toggle } = this;
    const max = content.scrollWidth - content.clientWidth;
    const left = Math.abs(content.scrollLeft);

    toggle.classList.toggle(PREV_CLASS, max > EDGE_TOLERANCE && left > EDGE_TOLERANCE);
    toggle.classList.toggle(NEXT_CLASS, max > EDGE_TOLERANCE && left < max - EDGE_TOLERANCE);
  }

  // Width an arrow covers at the edge; tabs are aligned to clear it.
  get zone() {
    return this.next.offsetWidth;
  }

  // Position of an item along the scrolled content, in px from its start.
  offsetOf(item) {
    const contentRect = this.content.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const start = itemRect.left - contentRect.left - this.content.clientLeft + this.content.scrollLeft;
    return { start, end: start + itemRect.width };
  }

  visibleItems() {
    return this.items().filter(item => item.offsetWidth > 0);
  }

  // Pages a whole view at a time, landing on a tab edge so no tab is left cut
  // off under the arrow: forward brings the first clipped tab to the left edge,
  // back brings the last clipped tab to the right edge.
  page(direction) {
    const { content, zone } = this;
    const viewStart = content.scrollLeft;
    const viewEnd = viewStart + content.clientWidth;
    const offsets = this.visibleItems().map(item => this.offsetOf(item));
    let target = null;

    if (direction > 0) {
      const clipped = offsets.find(offset => offset.end > viewEnd - zone + EDGE_TOLERANCE);
      target = clipped ? clipped.start - zone : null;
      if (target === null || target <= viewStart + EDGE_TOLERANCE) {
        target = viewStart + content.clientWidth - zone * 2;
      }
    } else {
      const clipped = offsets.reverse().find(offset => offset.start < viewStart + zone - EDGE_TOLERANCE);
      target = clipped ? clipped.end - content.clientWidth + zone : null;
      if (target === null || target >= viewStart - EDGE_TOLERANCE) {
        target = viewStart - content.clientWidth + zone * 2;
      }
    }

    this.scrollTo(target, true);
  }

  // Scrolls just enough to show the whole item clear of the arrows.
  reveal(item, smooth) {
    const { content, zone } = this;
    const offset = this.offsetOf(item);
    const viewStart = content.scrollLeft;
    const viewEnd = viewStart + content.clientWidth;

    if (offset.start < viewStart + (viewStart > 0 ? zone : 0)) {
      this.scrollTo(offset.start - zone, smooth);
    } else if (offset.end > viewEnd - zone) {
      this.scrollTo(offset.end - content.clientWidth + zone, smooth);
    }
  }

  scrollTo(target, smooth) {
    const { content, zone } = this;
    const max = content.scrollWidth - content.clientWidth;
    let left = Math.max(0, Math.min(max, target));

    // Within an arrow's width of either end, go all the way there, so the
    // first and last tabs always end up flush with the edge.
    if (left < zone) {
      left = 0;
    } else if (left > max - zone) {
      left = max;
    }

    content.scrollTo({ left, behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto' });
    this.schedule();
  }

  destroy() {
    this.mutationObserver.disconnect();
    TabScroll.resizeObserver?.unobserve(this.toggle);
    TabScroll.resizeObserver?.unobserve(this.content);
    if (this.frame !== null) {
      window.cancelAnimationFrame(this.frame);
    }
  }
}

TabScroll.instances = [];

TabScroll.resizeObserver = typeof window !== 'undefined' && window.ResizeObserver
  ? new ResizeObserver(entries => {
    entries.forEach(entry => {
      TabScroll.instances
        .find(instance => instance.toggle === entry.target || instance.content === entry.target)
        ?.schedule();
    });
  })
  : null;

TabScroll.bind = function (root) {
  if (root.hasAttribute(BOUND_ATTR)) {
    return;
  }
  const strip = STRIPS.find(candidate => root.matches(candidate.selector));
  if (!strip || strip.skip(root)) {
    return;
  }
  const content = strip.content(root);
  if (!content) {
    return;
  }
  if (!stylesApply(root)) {
    root.setAttribute(BOUND_ATTR, 'unstyled');
    return;
  }
  TabScroll.instances.push(new TabScroll(root, content, strip));
};

TabScroll.bindWithin = function (root) {
  if (root.nodeType !== Node.ELEMENT_NODE) {
    return;
  }
  if (root.matches(STRIP_SELECTOR)) {
    TabScroll.bind(root);
  }
  root.querySelectorAll(STRIP_SELECTOR).forEach(TabScroll.bind);
};

// Drops tab strips whose markup was replaced (AJAX panes and tabs reload often),
// so their observers don't keep detached nodes alive.
TabScroll.release = function () {
  TabScroll.instances = TabScroll.instances.filter(instance => {
    if (instance.toggle.isConnected) {
      return true;
    }
    instance.destroy();
    return false;
  });
};

function start() {
  TabScroll.bindWithin(document.body);

  new MutationObserver(mutations => {
    let removed = false;

    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(TabScroll.bindWithin);
      removed = removed || mutation.removedNodes.length > 0;
    });

    if (removed && TabScroll.instances.length > 0) {
      TabScroll.release();
    }
  }).observe(document.body, { childList: true, subtree: true });

  // Web fonts change tab widths after first paint.
  document.fonts?.ready.then(() => {
    TabScroll.instances.forEach(instance => instance.schedule());
  });
}

if (typeof window !== 'undefined') {
  window.TabScroll = TabScroll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}

export default TabScroll;
